import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import IntFileLogFilterForm from './IntFileLogFilterForm';
import { Button } from 'primereact/button';
import { isPermissionCodeValid } from '../../../utils/mics';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import moment from 'moment';

const IntFileLogList = ({ pageId }) => {

    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    // const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`)

    const { state } = useLocation();
    
    let navigate = useNavigate();

    const dispatch = useDispatch();

    const [dataList, setDataList] = useState([]);

    const [ fileId, setFileId ] = useState(state?.filterByFileId || null);
    
    const [deleteRecord, setDeleteRecord] = useState(null);

    const intFileLogList = useSelector((state) => state.intFileLog.intfileLogList);

     useEffect(() => {
         if (intFileLogList && intFileLogList?.data?.length > 0) {
             const parsedData = intFileLogList?.data?.map((log) => {
                 try {
                     if (log.logText) {
                         const jsonData = JSON.parse(log.logText);
                         return {
                             ...log,
                             url: jsonData.url || "",
                             request: jsonData.request || "",
                             response: jsonData.response || "",
                         };
                     }
                 } catch (error) {
                     console.error("Error parsing JSON:", error);
                 }
                 return { ...log, url: "", request: "", response: "" };
             });
             setDataList({ ...intFileLogList, data: parsedData });
         } else { 
               setDataList(intFileLogList);
         }
     }, [intFileLogList]);

    const filters = useSelector((state) => state.intFileLog.filters);

     const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        logType: "ERROR",
        sortOrder: -1,
        fileId: fileId,
        date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'), // now it's a string
        date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);

    const handleRefresh = useCallback(() => {
        dispatch(appActions.INTFILELOG_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    const handleSearch = useCallback((params) => {
        const searchParams = {
            ...params,
            pagesize: params.pagesize || defaultParams?.pagesize,
            pageno: params.pageno || defaultParams?.pageno,
            sort: params.sort || defaultParams?.sort,
            sortOrder: params.sortOrder || defaultParams?.sortOrder,
            logType: params.logType || defaultParams?.logType,
        };
    
        dispatch(appActions.INTFILELOG_GET_ALL_REQUEST(searchParams));
    }, [defaultParams, dispatch]);

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount

    const handleViewRedirect = useCallback((record) => {
        const data = dataList?.data?.find(t => t.id === record.id);
        navigate('/ui/integration-file-log/detail', { state: { data, isView: true } });
    }, [dataList, navigate]);
    
    const handleFormValuesChange = (values) => {
        setFormValues(values);
        setFileId(values?.fileId);
    };

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.INTFILELOG_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">Integration File Log</h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
                {/* <DownloadFile 
                    filePath={'/usr/dxp/logs/dxp-vssap-service.log'} 
                    tooltipText={'Download Application logs'}
                    btnLabel="App Logs"/> */}
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                {readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleDelete, readAccess, deleteAccess],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );

    const parseLogText = (logText) => {
        try {
            const json = JSON.parse(logText);
            if (typeof json === 'object' && json !== null) {
                return json?.response;
            }else{
                return logText
            }
        } catch (e) {
            return '';
        }
    }

    const columns = [
        { field: 'fileId', header: 'File ID', sortable: true, width: '120px', isLink : true, linkUrl : "/ui/integration-file" },
        { field: 'storeId', header: 'Store ID', sortable: true , width: '110px',},
        { field: 'logGroup', header: 'Log Group', sortable: true, width: '110px', },
        { field: 'logType', header: 'Log Type', width: '120px', },
        { field: 'logText', header: 'Log Text', sortable: true},
        // { field: 'url', header: 'URL', sortable: true},
        // { field: 'request', header: 'Request', sortable: true, template: logTextTemplate },
        // { field: 'response', header: 'Response', sortable: true, template: logTextTemplate },
        { field: 'createdOn', header: 'Created On', sortable: true, width: '120px', maxWidth: "150px", columnType: 'datetime' },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime' },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];

    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'> {renderHeader()} </div>
                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <IntFileLogFilterForm 
                        handleSearch={handleSearch} 
                        handleReset={handleRefresh} 
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={dataList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='integration_file_logs'
                        isLazy={true}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the file log : ${deleteRecord.id}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;
};

export default IntFileLogList;
